import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, Layers, CalendarDays, Users, Clock,
  Target, Boxes, Shield, Building2, Grid2x2,
  UserPlus, FileText, Lock, Globe, BookOpen, UserCog,
  SlidersHorizontal,
} from "lucide-react";
import type { PageNavItem } from "../../data/appNavConfig";

// ── Icon Map (shared pattern with SectionTabs) ──────────────────

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  "layout-dashboard": LayoutDashboard,
  layers: Layers,
  "calendar-days": CalendarDays,
  users: Users,
  clock: Clock,
  target: Target,
  boxes: Boxes,
  shield: Shield,
  "building-2": Building2,
  "grid-2x2": Grid2x2,
  "user-plus": UserPlus,
  "file-text": FileText,
  lock: Lock,
  globe: Globe,
  "book-open": BookOpen,
  "user-cog": UserCog,
  "sliders-horizontal": SlidersHorizontal,
};

const FallbackIcon = Grid2x2;

// ── Props ───────────────────────────────────────────────────────

export interface SideNavProps {
  items: PageNavItem[];
  activeItemPath: string;
  customContent?: React.ReactNode;
  style?: React.CSSProperties;
}

// ── SideNav ─────────────────────────────────────────────────────

export default function SideNav({ items, activeItemPath, customContent, style }: SideNavProps) {
  // Hide when no items and no custom content
  if (items.length === 0 && !customContent) return null;

  return (
    <aside className="shrink-0 border-r border-slate-200 bg-white flex flex-col overflow-y-auto" style={style}>
      {customContent ? (
        customContent
      ) : (
        <nav className="flex-1 px-3 pt-3 space-y-0.5">
          {items.map((item) => {
            const Icon = ICON_MAP[item.icon] || FallbackIcon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${
                    isActive
                      ? "bg-indigo-50 text-indigo-700"
                      : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                  }`
                }
              >
                <Icon
                  className={`size-4 shrink-0 ${
                    item.to === activeItemPath ? "text-indigo-600" : "text-slate-400"
                  }`}
                />
                <span className="flex-1 truncate">{item.label}</span>
                {item.badge !== undefined && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
      )}
    </aside>
  );
}
